import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { IdentificationCard, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";

const LOGO = "/org_logo.png";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { staff_id: staffId });
      setMsg(data.message);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-10 bg-background">
      <div className="absolute top-6 right-6"><ThemeToggle /></div>
      <div className="mx-auto w-full max-w-sm animate-fade-up">
        <Link to="/" data-testid="forgot-logo-home-link" aria-label="MELPWU home" className="block w-fit mx-auto mb-8"><img src={LOGO} alt="MELPWU logo" className="h-28 w-28 rounded-xl bg-white p-2 object-contain border border-border block" /></Link>
        {!sent ? (
          <>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Account recovery</span>
            <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Forgot password</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Enter your Staff ID or registered email and we'll email a secure reset link.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="staffId">Staff ID or email address</Label>
                <div className="relative">
                  <IdentificationCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="staffId" data-testid="forgot-staffid-input" value={staffId}
                    onChange={(e) => setStaffId(e.target.value)} placeholder="Staff ID or registered email"
                    className="pl-10 font-mono" required />
                </div>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
              <Button type="submit" data-testid="forgot-submit-button" disabled={busy} className="w-full h-11 text-base">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-sent">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
              <EnvelopeSimple size={28} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Check your email</h2>
            <p className="text-sm text-muted-foreground mt-2">{msg}</p>
            <p className="text-xs text-muted-foreground mt-4">
              No email on file? Ask your Regional or National Manager to reset your password.
            </p>
          </div>
        )}
        <button type="button" data-testid="back-to-login" onClick={() => navigate("/login")}
          className="mt-6 flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft size={16} /> Back to sign in
        </button>
      </div>
    </div>
  );
}
